import React, { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from "react";
import { uploadStatus } from "Components/GMS/Pdw/APIServices/Services";
import { TECHNICAL_ERROR_MESSAGE } from "Components/GMS/Rules/Components/RuleConstants";
import { AppContext } from "Context/AppContext";
import { toast } from "react-toastify";
import { getCategories, getCategoryTypes } from "../ARSServices/ARSServices";
import { RecommenderLogsResponse } from "../Types";

export const ArsContext = createContext<ArsContextType>({} as ArsContextType);

export const ArsContextProvider = ({ children }: PropsWithChildren) => {
    const appContext = useContext(AppContext);
    const industryId = appContext?.get_selected_industry()?.id;
    const channelKey = appContext?.channelKey;
    const [logsData, setLogsData] = useState<RecommenderLogsResponse[]>([]);
    const [model, setModel] = useState<RecommenderLogsResponse | null>(null);
    const [showStatus, setShowStatus] = useState<boolean>(false);
    const [showPromote, setShowPromote] = useState<boolean>(false);
    const [allCategories, setAllCategories] = useState<TCategory[]>([]);
    const [allCategoryTypes, setAllCategoryTypes] = useState<TCategoryType[]>([]);
    const [activeSubmodelIndex, setActiveSubmodelIndex] = useState<number>(0);
    const numberOfCategoriesToShow = 5;

    useEffect(() => {
        fetchCategoryTypes()
        fetchCategories()
    }, [industryId])

    useEffect(() => {
        if (!allCategories?.length || !allCategoryTypes?.length) {
            fetchCategoryTypes()
            fetchCategories()
        }
    }, [model?.model_id])

    const checkGenerateStatus = async (jobId: string) => {
        try {
            if (jobId) {
                setShowStatus(true);
                const response = await uploadStatus(jobId);
                if (response.errorfound) {
                    toast.error(TECHNICAL_ERROR_MESSAGE);
                    setShowStatus(false);
                    return;
                }

                if (response?.error) {
                    toast.error(response?.status);
                    setShowStatus(false);
                    return;
                }

                if (response?.processing) {
                    setTimeout(() => checkGenerateStatus(jobId), 2000);
                    return;
                }

                if (response?.done) {
                    toast.success(response?.message);
                    setShowStatus(false);
                    return;
                }
            } else {
                toast.error("Job Id not found");
            }
        } catch (error) {
            console.error("checkGenerateStatus ~ error:", error);
            toast.error(TECHNICAL_ERROR_MESSAGE);
            setShowStatus(false);
        }
    }

    const checkPromoteeStatus = async (jobId: string) => {
        try {
            if (jobId) {
                setShowPromote(true);
                const response = await uploadStatus(jobId);
                if (response.errorfound) {
                    toast.error(TECHNICAL_ERROR_MESSAGE);
                    setShowPromote(false);
                    return;
                }

                if (response?.error) {
                    toast.error(response?.status);
                    setShowPromote(false);
                    return;
                }

                if (response?.processing) {
                    setTimeout(() => checkPromoteeStatus(jobId), 2000);
                    return;
                }

                if (response?.done) {
                    toast.success(response?.message);
                    setShowPromote(false);
                    return;
                }
            } else {
                toast.error("Job Id not found");
            }
        } catch (error) {
            console.error("checkGenerateStatus ~ error:", error);
            toast.error(TECHNICAL_ERROR_MESSAGE);
            setShowPromote(false);
        }
    }

    const fetchCategoryTypes = async () => {
        if (!industryId) return;

        try {
            const response = await getCategoryTypes(industryId);
            if (response?.done) {
                setAllCategoryTypes(response?.data)
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to fetch category types");
        }
    }

    const fetchCategories = async () => {
        if (!industryId) return;
        try {
            const response = await getCategories(industryId);
            if (response?.done) {
                setAllCategories(response?.data)
            }
        } catch (error) {
            console.log(error);
            toast.error("Failed to fetch categories");
        }
    }

    return (
        <ArsContext.Provider value={{
            industryId,
            channelKey,
            logsData,
            setLogsData,
            model,
            setModel,
            checkGenerateStatus,
            showStatus,
            showPromote,
            checkPromoteeStatus,
            allCategories,
            allCategoryTypes,
            numberOfCategoriesToShow,
            activeSubmodelIndex,
            setActiveSubmodelIndex,
            fetchCategories,
            fetchCategoryTypes
        }}>
            {children}
        </ArsContext.Provider>
    );
}

export type ArsContextType = {
    industryId?: string
    channelKey?: string
    showStatus: boolean
    showPromote: boolean
    logsData: RecommenderLogsResponse[]
    setLogsData: Dispatch<SetStateAction<RecommenderLogsResponse[]>>
    model: RecommenderLogsResponse | null
    setModel: Dispatch<SetStateAction<RecommenderLogsResponse | null>>
    checkGenerateStatus: (jobId: string) => Promise<void>
    checkPromoteeStatus: (jobId: string) => Promise<void>
    allCategories: TCategory[]
    allCategoryTypes: TCategoryType[]
    numberOfCategoriesToShow: number
    activeSubmodelIndex: number
    setActiveSubmodelIndex: React.Dispatch<React.SetStateAction<number>>
    fetchCategoryTypes: () => Promise<void>
    fetchCategories: () => Promise<void>
}